import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { IHttpErrorResponseCustomized } from '@eai-tdv/core/models';
import { DateTimeFormatService } from '@eai-tdv/core/services/date-time-format';
import { IRunAiPayload } from '@eai-tdv/core/services/resources-http';

import { DialogService } from '@eai-tdv/shared/dialogs';

import { ToastrService } from '../../shared/components/toast';
import { AuthUserFacadeService } from '../services/auth-user-facade.service';
import { NavigationWebService } from '../services/navigation.service';
import {
  isSecureUrlBySubscriptions,
  SubscriptionsService,
} from '../services/subscriptions.service';

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  private forbidden403lastProfileFetchTimestamp?: number;

  constructor(
    private readonly authUserFacadeS: AuthUserFacadeService,
    private readonly toastrS: ToastrService,
    private readonly dialogS: DialogService,
    private readonly navigationWebS: NavigationWebService,
    private readonly subscriptionsS: SubscriptionsService,
  ) {}

  public intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError(_err => {
        if (_err instanceof HttpErrorResponse) {
          const error = _err as IHttpErrorResponseCustomized;

          if (error.status === 400) {
            // this case for handle on BE some binars. Example - upload to BE image with wrong extension or other cases.
            // if you need some specific logic - add it to component and add here specific url like exception
            this.toastrS.handleHttpErr(error);
          }

          //  401
          if (error.status === 401) {
            // logic will be handled in jwt interceptor
            // ! WITHOUT TOASTR - toastr will be handled via components/not generic approach
            // if you need some specific logic - add it to component
          }

          // 403
          // here only functionality logic WITH SHOW TOASTRS.
          // if you need some specific logic - add it to component and add here specific url like exception for toastr
          if (error.status === 403) {
            if (
              error.error.error_code === 'error.user.deactivated' ||
              error.error.error_code === 'error.auth.permission_changed'
            ) {
              this.authUserFacadeS.onLogoutFlow();
            } else if (
              isSecureUrlBySubscriptions(request.url) &&
              error.error.error_code === 'error.auth.has_no_active_subscription'
            ) {
              this.showNoActiveSubModal();
            } else if (
              isSecureUrlBySubscriptions(request.url) &&
              error.error.error_code === 'error.ai_analysis.no_credits'
            ) {
              this.showNoCreditsLeftModal(request.body as IRunAiPayload);
            } else if (
              // not australia - logout
              error.error.error_code === 'error.auth.geolocation_restricted'
            ) {
              this.authUserFacadeS.onLogoutFlow();
            } else if (
              // single session
              error.error.error_code === 'error.auth.single_session'
            ) {
              this.authUserFacadeS.onLogoutFlow({
                afterLogoutCallback: () => {
                  this.showSingleSessionWarningModal();
                },
              });
            } else {
              // exist possbility that account was changed by admin - then refetch account with new permissions
              if (
                (this.authUserFacadeS.getAuthProfile() &&
                  !this.forbidden403lastProfileFetchTimestamp) ||
                new Date().getTime() -
                  (this.forbidden403lastProfileFetchTimestamp || 0) >
                  5000
              ) {
                this.forbidden403lastProfileFetchTimestamp =
                  new Date().getTime();
                this.authUserFacadeS.fetchAuthProfile().subscribe();
              }
            }
            this.toastrS.handleHttpErr(error);
          }

          // 422
          if (error.status === 422) {
            // mostly forms fields
            // !!! WITHOUT TOASTR HERE. add it locally if you need with catchError
          }

          // 429 - too many requests
          if (error.status === 429) {
            this.toastrS.handleHttpErr(error);
          }

          // 500
          if (error.status === 500) {
            this.toastrS.show500HttpErr(request.url);
          }

          // 504
          if (error.status === 0) {
            this.toastrS.showNoNetwork();
          }
        }
        console.error('HTTP ERROR', _err);
        return throwError(() => _err);
      }),
    );
  }

  // todo - remove? ask Sergei. most possible dont need anymore
  private showNoCreditsLeftModal(body: IRunAiPayload): void {
    const sub = this.subscriptionsS.subscriptionSgnl() || null;
    const date = sub?.quota_reset_at
      ? DateTimeFormatService.transformApiToUi(sub.quota_reset_at, 'date')
      : '';
    if (date) {
      this.dialogS
        .openResult({
          type: 'warning',
          titleLocaliKey: 'Quota Reached',
          descriptionSegmentedText: [
            {
              textLocaliKey:
                'You have reached your quota. Your quota will automatically reset on ',
            },
            {
              textLocaliKey: date,
              isBold: true,
            },
          ],
          okBtnTitleLocaliKey: 'Close',
        })
        .result$.subscribe(() => {
          this.subscriptionsS.fetchSubscription().subscribe();
        });
    } else {
      console.warn('Something wrong with Sub quota reset date');
    }
  }

  private showSingleSessionWarningModal(): void {
    this.dialogS.openResult({
      type: 'info',
      titleLocaliKey: "You've been logged out!",
      descriptionLocaliKey:
        'Your session has ended because you signed in on another device. Only one active session is allowed per user.',
      okBtnTitleLocaliKey: 'Ok',
      backdropWithBlur: true,
    });
  }

  private showNoActiveSubModal(): void {
    this.dialogS
      .openResult({
        type: 'warning',
        titleLocaliKey: 'No Active Subscription',
        descriptionLocaliKey:
          'You don’t have an active subscription. Please visit the Subscription & Billing settings to choose your plan.',
        okBtnTitleLocaliKey: 'Subscription & Billing',
      })
      .result$.subscribe(res => {
        if (res.okClicked) {
          this.subscriptionsS.fetchSubscription().subscribe();
          this.navigationWebS.goToProfileSubscriptionsAndBilling();
        }
      });
  }
}
